import styled, { css } from "styled-components";
import { darken, lighten, tint } from "polished";
import { borderRadius, color, focusRing, highContrastStyle, localReset, onHover, supportsHasSelector, transition, typeStyle, units, visuallyHidden } from "@m/alchemy-theme";
import { ArrowRightIcon } from "../icons/ArrowRightIcon";
import { ConfirmCheckmarkIcon } from "../icons/ConfirmCheckmarkIcon";
import { ImportantIcon } from "../icons/ImportantIcon";
import { NakedButton } from "../naked-button/NakedButton";
const HIT_AREA_OFFSET = 0.5;
export const EDGE_PADDING = units(3);
export const EDGE_PADDING_ICON = units(5);
export const EDGE_PADDING_NARROW = units(2);
const verticalPadding = units(1);
const compensateScrollForPadding = css `
  scroll-margin-top: ${verticalPadding};
  scroll-margin-bottom: ${verticalPadding};
`;
const actAsRightEdge = css `
  &:last-child {
    margin-right: ${EDGE_PADDING};
  }

  &:not(:last-child) {
    padding-right: ${units(1.5)};
  }
`;
const highlightedDescriptionColor = () => css `
  ${(props) => darken(highContrastStyle(0.15, 0.025)(props), color("typeMeta")({ ...props, palette: "interactiveConstructive" }))}
`;
const itemMinHeight = (props) => (props.density === "compact" ? "32px" : "48px");
export const InfoIcon = styled(ImportantIcon) `
  color: ${color("base", { palette: "interactiveNavigational" })};
  transition: ${transition("ease", "background-color")};
`;
export const InfoIconButton = styled(NakedButton) `
  display: inline-flex;
  margin: -${units(HIT_AREA_OFFSET)} ${units(0.25)};
  padding: ${units(HIT_AREA_OFFSET)};
  border-radius: ${borderRadius("extraLarge")};
  // TODO: re-add in high contrast mode?
  // background-color: ${color("container")};
  transition: ${transition("base", "background-color")};

  ${InfoIcon} {
    transition: ${transition("base", "color")};
  }

  ${onHover(css `
    background-color: ${color("highlight", { opacity: 0.4, palette: "interactiveConstructive" })};

    ${InfoIcon} {
      color: ${color("base", { palette: "interactiveConstructive" })};
    }
  `)}

  // used instead of aria-expanded to pass automated accessibility checks
  &[data-popover-expanded="true"] {
    background-color: ${color("highlight", { opacity: 0.4, palette: "interactiveConstructive" })};

    ${InfoIcon} {
      color: ${color("base", { palette: "interactiveConstructive" })};
    }
  }
`;
export const IconWrapper = styled.div `
  grid-area: icon;
  margin-left: ${units(2)};
  display: flex;

  ${typeStyle("bodyS")};
  line-height: 1.2;

  & > svg {
    display: inline-block;
    vertical-align: middle;
    color: ${color("typeMeta")};
  }
`;
export const EmptyMenuListItem = styled.li `
  ${typeStyle("bodyS")};
  align-items: center;
  color: ${color("typeMeta")};
  display: flex;
  justify-content: center;
  min-height: ${itemMinHeight};
  padding: ${verticalPadding} ${EDGE_PADDING};
`;
export const PrimaryAction = styled.button `
  ${localReset()}

  flex-grow: 1;

  display: grid;
  grid-template:
    "icon title" auto
    ". description" auto / ${EDGE_PADDING} auto;
  align-items: start;
  word-break: break-word;
  ${typeStyle("bodyS")};
  ${({ hasIcon }) => hasIcon && `grid-template-columns: ${EDGE_PADDING_ICON} auto;`}
  ${actAsRightEdge}
  ${compensateScrollForPadding}
`;
export const GroupLabel = styled.div `
  color: ${color("typeMeta")};
  grid-area: title;
  ${typeStyle("labelL")};
  line-height: 1.21;
  margin-bottom: ${units(0.5)};
`;
export const Label = styled.div `
  ${localReset()}
  grid-area: title;
  ${typeStyle("bodyS")};
  line-height: 1.2;

  text-align: start;

  > a {
    display: block;
    text-decoration: none;
    color: ${color("typeLink")};

    &:focus {
      outline: none;
    }
  }
`;
export const Description = styled.div `
  grid-area: description;

  color: var(--MenuListItem--description-color, ${color("typeMeta")});
  transition: color 200ms ease;
  ${typeStyle("captionM")};
  line-height: 1.21;
  margin-top: ${units(0.5)};
`;
export const EndWrapper = styled.div `
  flex-grow: 1;
  justify-content: flex-end;
  max-width: 40%;
`;
export const Details = styled(EndWrapper) `
  ${typeStyle("bodyS")};
  line-height: 1.2;
  text-align: right;
  word-break: break-word;
  ${actAsRightEdge}
`;
export const ExpandIcon = styled(ArrowRightIcon) `
  flex-shrink: 0;
  transition: transform 200ms ease;
  ${({ isExpanded }) => (isExpanded ? `transform: rotate(90deg)` : ``)};

  ${actAsRightEdge}
`;
export const ParentForwardWrapper = styled.button `
  ${localReset()}
  ${actAsRightEdge}
`;
export const ParentForwardIcon = styled(ArrowRightIcon) ``;
export const SelectedIconWrapper = styled("div") `
  grid-area: icon;
  margin-left: auto;
  margin-right: ${units(0.5)};

  ${typeStyle("bodyS")};
  line-height: 1.2;
`;
export const SelectedIcon = styled(ConfirmCheckmarkIcon) `
  opacity: 0;
  transition: all 100ms ease;
  ${({ isSelected }) => isSelected && `opacity: 1;`};

  display: inline-block;
  vertical-align: middle;
  transform: translateY(-1px);

  & path:is(.ConfirmCheckmarkIcon__svg_mark) {
    stroke: currentColor;
    stroke-width: 0.5;
  }
`;
export const Separator = styled.li `
  border-bottom: 1px solid ${color("line")};
  margin-bottom: ${units(0.5)};
  padding-bottom: ${units(0.5)};
`;
export const LoadingWrapper = styled.li `
  min-height: ${itemMinHeight};
  padding: ${units(0.5)} 0;
  position: relative;
`;
export const VisuallyHiddenLabel = styled.span `
  ${visuallyHidden()}
`;
export const Item = styled.li `
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-grow: 1;
  min-height: ${itemMinHeight};
  padding: ${verticalPadding} 0;
  position: relative;
  text-align: left;
  transition: background-color 200ms ease;
  user-select: none;
  width: 100%;

  ${({ isDisabled }) => isDisabled &&
    css `
      cursor: not-allowed;
      color: ${color("typeMeta")};
    `};

  ${({ isSelected, ...props }) => isSelected &&
    css `
      background-color: ${color("hint")({ ...props, palette: "interactiveConstructive" })};
      --MenuListItem--description-color: ${highlightedDescriptionColor};
    `}

  // cancel the built-in outline on the primary target (menu item, listbox option, link)
  [data-whatinput="keyboard"] & [data-primary-focus-target]:focus,
  [data-whatinput="keyboard"] &:focus {
    outline: none;
  }

  ${supportsHasSelector(css `
      &:hover,
      [data-whatinput="keyboard"] &:has([data-primary-focus-target]:focus),
      [data-whatinput="keyboard"] &:focus {
        background-color: ${({ ...props }) => tint(0.4, color("hint")({ ...props, palette: "interactiveConstructive" }))};
        --MenuListItem--description-color: ${highlightedDescriptionColor};
      }

      [data-whatinput="keyboard"] &:has([data-primary-focus-target]:focus),
      [data-whatinput="keyboard"] &:focus {
        ${focusRing({ type: "inner" })};
      }
    `, {
    fallbackStyles: css `
        &:hover,
        [data-whatinput="keyboard"] &:focus-within {
          background-color: ${({ ...props }) => tint(0.4, color("hint")({ ...props, palette: "interactiveConstructive" }))};
          --MenuListItem--description-color: ${highlightedDescriptionColor};
        }

        [data-whatinput="keyboard"] &:focus-within {
          ${focusRing({ type: "inner" })};
        }
      `
})}

  &:hover ${InfoIcon}, [data-whatinput="keyboard"] &:focus-within ${InfoIcon} {
    /* TODO: clarify if needed or should be local to ItemDetails component */
    opacity: 1;
  }

  &:hover ${IconWrapper} > svg,
  [data-whatinput="keyboard"] &:focus-within ${IconWrapper} > svg {
    color: ${(props) => color("base")({ ...props, palette: "interactiveConstructive" })};
  }
`;
export const NavItem = styled(Item) `
  ::before {
    display: none;
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${units(0.5)};
    min-height: ${units(4.5)};
    height: 85%;
    width: 3px;
    background-color: ${color("base", {
    palette: "interactiveConstructive"
})};
    border-radius: ${units(0.25)};
  }

  // TODO: do something with the marker in focused state

  ${(props) => onHover(css `
        background-color: ${lighten(0.015, color("hint", { palette: "interactiveConstructive" })(props))};

        ::before {
          display: block;
          background-color: ${color("highlight", {
    palette: "interactiveConstructive"
})};
        }
      `, { includeActive: true })}

  ${({ isActive }) => isActive &&
    css `
      background-color: ${color("hint", {
        palette: "interactiveConstructive"
    })};
      font-weight: bold;

      ::before {
        display: block;
      }

      /* no hover effects for active items */
      ${() => onHover(css `
            background-color: ${color("hint", {
        palette: "interactiveConstructive"
    })};

            ::before {
              background-color: ${color("base", {
        palette: "interactiveConstructive"
    })};
            }
          `, { includeActive: true })}
    `};

  // TODO: propagate prop onto buttons instead of using CSS
  a,
  button {
    ${({ isDisabled }) => isDisabled &&
    css `
        cursor: not-allowed;
        color: ${color("typeMeta")};
      `};
  }
`;
